<mat-dialog-content>
  <div class="item-modal">
    <form [formGroup]="editItemForm">
      <mat-stepper linear #stepper>
        <!-- ============== STEP 1: EDIT ITEM ============== -->
        <mat-step [stepControl]="prepGroup" [editable]="true">
          <ng-template matStepLabel>Edit Item</ng-template>

          @if (baseItemGroupEntity.isCommodityGroup) {
            <div>{{ baseItemGroupEntity.commodityGroupName }}</div>
          }
          @for (group of baseItemGroupEntity.itemGroups; track group) {
            <div>
              @if (group.isExternalItemNumberGroup) {
                <div class="info">
                  <div class="item-name">Commodity Code {{ group.itemNumber }}</div>
                  @for (item of group.items; track item) {
                    <div class="sub-id">{{ item.itemName }} - {{ item.itemDescription }}</div>
                  }
                </div>
              }
              @if (!group.isExternalItemNumberGroup) {
                <div class="info">
                  <div class="item-name">{{ group.items[0].itemName }} / {{ group.items[0].itemDescription }}</div>
                  <div class="sub-id">#{{ group.items[0].commerceItemNumber }}</div>
                </div>
              }
            </div>
          }

          <mat-accordion multi>
            <!-- Item Preparation -->
            <mat-expansion-panel formGroupName="prep">
              <mat-expansion-panel-header class="panel-title-heading">
                <mat-panel-title>Item Preparation</mat-panel-title>
              </mat-expansion-panel-header>
              <div>
                <div class="prep-location-row">
                  <mat-label>
                    Prep Location
                    @if (isPrepLocationItemEdited) {
                      <span class="edited-label">EDITED</span>
                    }
                  </mat-label>
                  <mat-form-field class="prep-dept-dropdown">
                    <mat-label>Who will prepare this item?</mat-label>
                    <mat-select formControlName="prepLocation" required>
                      @for (pl of prepLocations; track pl) {
                        <mat-option [value]="pl.prepLocationTypeName">{{ pl.prepLocationTypeName }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                </div>
                <div class="item-times-row">
                  <mat-label>
                    Item Times
                    @if (isItemTimesItemEdited) {
                      <span class="edited-label">EDITED</span>
                    }
                  </mat-label>
                </div>
                <div class="counter-row" style="display: flex; gap: 24px">
                  <div class="counter" [style.flex]="!adminOrITRole ? '1' : 'none'">
                    <mat-label class="caption">
                      Lead Time
                      @if (adminOrITRole) {
                        <span>(Hours)</span>
                      }
                    </mat-label>
                    <increment-input
                      class="caption"
                      formControlName="itemChangeLeadHours"
                      [step]="1"
                      [min]="0"
                      [max]="24"
                      [showUnitLabel]="!adminOrITRole"
                      unit="Hours">
                    </increment-input>
                  </div>
                  <div class="counter" [style.flex]="!adminOrITRole ? '1' : 'none'">
                    <mat-label class="caption">
                      Prep Time
                      @if (adminOrITRole) {
                        <span>(Minutes)</span>
                      }
                    </mat-label>
                    <increment-input
                      class="caption"
                      formControlName="prepMinutes"
                      [step]="1"
                      [min]="0"
                      [max]="1440"
                      [showUnitLabel]="!adminOrITRole"
                      unit="Minutes">
                    </increment-input>
                  </div>
                  @if (adminOrITRole) {
                    <div class="counter">
                      <mat-label class="caption">Post-Prep Buffer (Minutes)</mat-label>
                      <increment-input
                        class="caption"
                        formControlName="postPrepMinutes"
                        [step]="1"
                        [min]="0"
                        [max]="1440"
                        [showUnitLabel]="!adminOrITRole">
                      </increment-input>
                    </div>
                  }
                </div>
              </div>
            </mat-expansion-panel>

            <!-- Pickup Dates / Quantities / Sold Dates -->
            <mat-expansion-panel>
              <mat-expansion-panel-header class="panel-title-heading">
                <mat-panel-title>Pickup Dates</mat-panel-title>
              </mat-expansion-panel-header>
              <div class="form-section">
                <div class="form-field-group pickup-dates-row" formGroupName="pickup">
                  <mat-label class="section-label">
                    Pickup Dates
                    @if (isPickupDatesItemEdited) {
                      <span class="edited-label">EDITED</span>
                    }
                  </mat-label>
                  <mat-radio-group
                    class="radio-group"
                    formControlName="isDateRestricted"
                    name="date-restriction"
                    [disabled]="!adminOrITRole">
                    <mat-radio-button [value]="false">Any day</mat-radio-button>
                    <mat-radio-button [value]="true">Set a date range</mat-radio-button>
                  </mat-radio-group>

                  @if (isDateRestricted) {
                    <div class="date-restrictions-container" formGroupName="dates">
                      @if (pickupDatesForm.errors) {
                        <div class="validation-errors">
                          @if (pickupDatesForm.errors.dateRangeWidth) {
                            <div class="error">Start and End Date must be within 120 days of each other</div>
                          }
                          @if (pickupDatesForm.errors.startDateBackdated) {
                            <div class="error">Start Date must be within the last 120 days</div>
                          }
                          @if (pickupDatesForm.errors.endBeforeStart) {
                            <div class="error">Start Date must be before End Date</div>
                          }
                        </div>
                      }
                      <div class="date-picker-row">
                        <mat-form-field appearance="fill" class="date-field">
                          <mat-label>Start Pickup Date</mat-label>
                          <input
                            matInput
                            [matDatepicker]="pickupStartDate"
                            formControlName="startDate"
                            [max]="maxPickupStartDate" />
                          <mat-hint>MM/DD/YYYY</mat-hint>
                          <mat-datepicker-toggle matSuffix [for]="pickupStartDate"></mat-datepicker-toggle>
                          <mat-datepicker #pickupStartDate></mat-datepicker>
                        </mat-form-field>
                        <mat-form-field appearance="fill" class="date-field">
                          <mat-label>End Pickup Date</mat-label>
                          <input
                            matInput
                            [matDatepicker]="pickupEndDate"
                            formControlName="endDate"
                            [min]="minPickupEndDate" />
                          <mat-hint>MM/DD/YYYY</mat-hint>
                          <mat-datepicker-toggle matSuffix [for]="pickupEndDate"></mat-datepicker-toggle>
                          <mat-datepicker #pickupEndDate></mat-datepicker>
                        </mat-form-field>
                      </div>
                    </div>
                  }
                </div>

                @if (isDateRestricted && doesCurrentAccountHaveAnyRole(["IT", "Admin", "Division"])) {
                  <div class="form-field-group sellable-qty-row" formGroupName="quantity">
                    <mat-label class="section-label">
                      Quantities
                      @if (isSellableQtyItemEdited) {
                        <span class="edited-label">EDITED</span>
                      }
                    </mat-label>
                    <mat-radio-group
                      class="radio-group"
                      formControlName="isSellableQuantityEnabled"
                      name="limited-quantity"
                      [disabled]="!adminOrITRole">
                      <mat-radio-button [value]="false">Unlimited Quantity</mat-radio-button>
                      <mat-radio-button [value]="true">Limit Quantity for date range</mat-radio-button>
                    </mat-radio-group>
                    @if (isSellableQuantityEnabled) {
                      <div class="quantity-input-container">
                        <mat-form-field appearance="fill" class="quantity-field">
                          <mat-label>Total Sellable Quantity</mat-label>
                          <input matInput type="number" formControlName="sellableQuantity" />
                        </mat-form-field>
                      </div>
                    }
                  </div>
                }

                @if (isDateRestricted && isSellableQuantityEnabled) {
                  <div class="form-field-group sold-dates-row" formGroupName="quantity">
                    <mat-label class="section-label">
                      Sold Dates
                      @if (isSoldDatesItemEdited) {
                        <span class="edited-label">EDITED</span>
                      }
                    </mat-label>
                    <div class="date-restrictions-container" formGroupName="soldDates">
                      @if (soldDatesForm.errors) {
                        <div class="validation-errors">
                          @if (soldDatesForm.errors.dateRangeWidth) {
                            <div class="error">Start and End Date must be within 120 days of each other</div>
                          }
                          @if (soldDatesForm.errors.startDateBackdated) {
                            <div class="error">Start Date must be within the last 120 days</div>
                          }
                          @if (soldDatesForm.errors.endBeforeStart) {
                            <div class="error">Start Date must be before End Date</div>
                          }
                        </div>
                      }
                      <div class="date-picker-row">
                        <mat-form-field appearance="fill" class="date-field">
                          <mat-label>Start Date</mat-label>
                          <input
                            matInput
                            [matDatepicker]="soldStartDate"
                            formControlName="startDate"
                            [max]="maxSoldStartDate" />
                          <mat-hint>MM/DD/YYYY</mat-hint>
                          <mat-datepicker-toggle matSuffix [for]="soldStartDate"></mat-datepicker-toggle>
                          <mat-datepicker #soldStartDate></mat-datepicker>
                        </mat-form-field>
                        <mat-form-field appearance="fill" class="date-field">
                          <mat-label>End Date</mat-label>
                          <input
                            matInput
                            [matDatepicker]="soldEndDate"
                            formControlName="endDate"
                            [min]="minSoldEndDate" />
                          <mat-hint>MM/DD/YYYY</mat-hint>
                          <mat-datepicker-toggle matSuffix [for]="soldEndDate"></mat-datepicker-toggle>
                          <mat-datepicker #soldEndDate></mat-datepicker>
                        </mat-form-field>
                      </div>
                    </div>
                  </div>
                }
              </div>
            </mat-expansion-panel>

            <!-- Available Handoff Types -->
            <mat-expansion-panel formGroupName="handoffTypes">
              <mat-expansion-panel-header class="panel-title-heading">
                <mat-panel-title>Available Handoff Types</mat-panel-title>
              </mat-expansion-panel-header>
              <div>
                <div class="handoff-types-row">
                  <mat-label>
                    Available Types
                    @if (isAvailableTypesItemEdited) {
                      <span class="edited-label">EDITED</span>
                    }
                  </mat-label>
                  <div class="handoff-types">
                    @if (handoffTypesForm.errors?.["noneSelected"]) {
                      <span class="handoff-type-error">*At least 1 hand-off type must be selected</span>
                    }
                    <mat-checkbox class="handoff-type" formControlName="carryout">Carryout</mat-checkbox>
                    <mat-checkbox class="handoff-type" formControlName="curbside">Curbside</mat-checkbox>
                    <mat-checkbox class="handoff-type" formControlName="delivery">Delivery</mat-checkbox>
                    <mat-checkbox class="handoff-type" formControlName="kiosk">Kiosk</mat-checkbox>
                    <mat-checkbox class="handoff-type" formControlName="tableside">Tableside</mat-checkbox>
                  </div>
                </div>
              </div>
            </mat-expansion-panel>

            <!-- Apply to other items / stores -->
            <mat-expansion-panel formGroupName="apply">
              <mat-expansion-panel-header class="panel-title-heading">
                <mat-panel-title>Apply these edits to other items or stores?</mat-panel-title>
              </mat-expansion-panel-header>
              @if (changeSummary.length > 0) {
                <div class="change-summary">
                  <div>Edits:</div>
                  @for (edit of changeSummary; track edit.label) {
                    <div>{{ edit.description }}</div>
                  }
                </div>
              }
              @if (adminOrITRole) {
                <div class="footerSection">
                  @if (!baseItemGroupEntity.isCommodityGroup) {
                    <div>
                      <mat-checkbox formControlName="isMultiItem">
                        Yes, apply these changes to additional <u>items</u>
                      </mat-checkbox>
                    </div>
                  }
                  <div>
                    <mat-checkbox formControlName="isMultiStore">
                      Yes, apply these changes to additional <u>stores</u>
                    </mat-checkbox>
                  </div>
                </div>
              }
            </mat-expansion-panel>
          </mat-accordion>

          <div class="button-container">
            <button mat-button color="warn" (click)="onNoClick()">Exit</button>
            @if (!isMultiAny()) {
              <button
                mat-button
                matStepperNext
                mat-raised-button
                color="accent"
                (click)="updateFacilityItems()"
                type="button"
                [disabled]="!isConfirmClickable">
                Confirm
              </button>
            }
            @if (isMultiItem) {
              <button
                mat-button
                matStepperNext
                mat-raised-button
                color="accent"
                type="button"
                [disabled]="!isConfirmClickable">
                Select Items
              </button>
            }
            @if (isMultiStore && !isMultiItem) {
              <button
                mat-button
                matStepperNext
                mat-raised-button
                color="accent"
                type="button"
                [disabled]="!isConfirmClickable">
                Select Stores
              </button>
            }
          </div>
        </mat-step>

        <!-- ============== STEP 2: SELECT ITEMS ============== -->
        @if (isMultiItem && !baseItemGroupEntity.isCommodityGroup) {
          <mat-step [stepControl]="applyGroup" [editable]="true">
            <ng-template matStepLabel>Select Items</ng-template>
            <ng-template matStepContent>
              <!-- header markup unchanged from original; omitted for brevity -->
              <multi-item-checklist
                [(relatedItems)]="selectedItemGroups"
                [item]="baseItemGroups[0]"
                [allItems]="menuItems"
                (validationCheck)="itemValidation()">
              </multi-item-checklist>
              <div class="button-container">
                <button mat-button matStepperPrevious type="button">Back</button>
                @if (!isMultiStore) {
                  <button
                    mat-button
                    matStepperNext
                    mat-raised-button
                    color="accent"
                    (click)="updateFacilityItems()"
                    type="button"
                    [disabled]="!isConfirmClickable">
                    Confirm
                  </button>
                }
                @if (isMultiStore) {
                  <button
                    mat-button
                    matStepperNext
                    mat-raised-button
                    color="accent"
                    type="button"
                    [disabled]="!isConfirmClickable">
                    Select Stores
                  </button>
                }
              </div>
            </ng-template>
          </mat-step>
        }

        <!-- ============== STEP 3: SELECT STORES ============== -->
        @if (isMultiStore) {
          <mat-step [stepControl]="applyGroup" [editable]="true">
            <ng-template matStepLabel>Select Stores</ng-template>
            <ng-template matStepContent>
              <multi-store-dropdown
                [(facilities)]="facilities"
                [currentFacilityId]="currentFacilityId"
                (validationCheck)="facilityValidation()">
              </multi-store-dropdown>
              <div class="button-container">
                <button mat-button matStepperPrevious type="button">Back</button>
                <button
                  mat-button
                  mat-raised-button
                  color="accent"
                  (click)="updateFacilityItems()"
                  type="button"
                  [disabled]="!isConfirmClickable">
                  Confirm
                </button>
              </div>
            </ng-template>
          </mat-step>
        }
      </mat-stepper>
    </form>
  </div>
</mat-dialog-content>
